import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuCirclePause, LuCirclePlay, LuFolderKanban, LuRocket } from 'react-icons/lu';
import DataCardList from '../../../components/data/DataCardList';
import DataTable from '../../../components/data/DataTable';
import DataToolbar from '../../../components/data/DataToolbar';
import FilterSheet from '../../../components/data/FilterSheet';
import PageHero from '../../../components/data/PageHero';
import StatTiles from '../../../components/data/StatTiles';
import TablePagination from '../../../components/data/TablePagination';
import useElementWidth from '../../../hooks/useElementWidth';
import useMediaQuery from '../../../hooks/useMediaQuery';
import usePagedList from '../../../hooks/usePagedList';
import useTableColumns from '../../../hooks/useTableColumns';
import useViewportFit from '../../../hooks/useViewportFit';
import BudgetModal from './BudgetModal';
import ContractsModal from './ContractsModal';
import useProjectFinance from './useProjectFinance';
import useProjectFinanceText from './projectFinanceText';
import {
  ALL_PHASES,
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  MIN_COLUMN_WIDTH,
  PF_COLUMNS,
  PHASE_VALUES,
  PINNED_COLUMNS,
  getFilterState,
} from './projectFinanceColumns';
import ProjectFinanceCell, { ProjectFinanceCardRow, useProjectCellText } from './parts/ProjectFinanceCell';
import ProjectFinanceDetailPane from './parts/ProjectFinanceDetailPane';

const COLUMNS_STORAGE_KEY = 'projectFinance.tableColumns.v1';
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Proje Finans Yönetimi (Tailwind), built like the other data pages: phase
 * tiles that filter, a table card that reaches at most the bottom of the
 * screen (phase switch, header filters, column menu) and the selected
 * project's panel with Bütçe and Sözleşmeler.
 */
const ProjectsFinanceManagementPage = () => {
  const {
    projects, isLoading, filteredProjects, stats, filters, handleFilter, clearFilters, searchTerm, setSearchTerm,
    phaseGroup, setPhaseGroup, selectedProject, setSelectedId,
  } = useProjectFinance();
  const { language, locale, label, phaseLabel } = useProjectFinanceText();
  const cellText = useProjectCellText();

  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: PF_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  // Sayfa başına sabit 10 satır (kullanıcı isteği): kart ekrana sığdırılmaz, tablo satır sayısını ölçmez
  const maxCardHeight = undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [dialog, setDialog] = useState(null); // budget | contracts
  const hasLoaded = projects.length > 0 || !isLoading;

  const resetKey = JSON.stringify([filters, searchTerm, phaseGroup]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(filteredProjects, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  useEffect(() => {
    if (!selectedProject) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedId(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedProject, showFilterSheet, setSelectedId]);

  const columnLabel = useCallback((key) => label(`columns.${key}`), [label]);
  const phaseOptions = useMemo(() => PHASE_VALUES.map((value) => ({ value, label: phaseLabel(value) })), [phaseLabel]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: { type: PF_COLUMNS[key].type, options: key === 'projectPhase' ? phaseOptions : undefined },
  })), [columns.visibleKeys, columnLabel, phaseOptions]);

  const filterChips = Object.keys(filters)
    .filter((key) => PF_COLUMNS[key] && getFilterState(key, filters[key]) !== 'off')
    .map((key) => ({ key, label: columnLabel(key), value: key === 'projectPhase' ? phaseLabel(filters[key]) : String(filters[key]), state: 'applied' }));

  const isFilterActive = Object.values(filters).some(Boolean) || Boolean(searchTerm) || phaseGroup !== ALL_PHASES;

  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedProject ? filteredProjects.findIndex((project) => project.id === selectedProject.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredProjects[selectedIndex + offset];
    if (!sibling) return;
    setSelectedId(sibling.id);
    reveal(sibling.id);
  };

  const toggleGroup = (group) => setPhaseGroup(phaseGroup === group ? ALL_PHASES : group);
  const shareOf = (count) => (stats.total > 0 ? Math.min(1, count / stats.total) : 0);
  const shareTitle = (count) => label('stats.share', { percent: Math.round(shareOf(count) * 100) });
  const tiles = [
    { key: 'total', label: label('stats.total'), shortLabel: label('stats.totalShort'), value: stats.total, icon: LuFolderKanban, gradient: 'tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30', onClick: clearFilters, active: !isFilterActive },
    { key: 'active', label: label('stats.active'), shortLabel: label('stats.activeShort'), value: stats.active, icon: LuCirclePlay, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', share: shareOf(stats.active), shareTitle: shareTitle(stats.active), barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500', onClick: () => toggleGroup('active'), active: phaseGroup === 'active' },
    { key: 'passive', label: label('stats.passive'), shortLabel: label('stats.passiveShort'), value: stats.passive, icon: LuCirclePause, gradient: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30', onClick: () => toggleGroup('passive'), active: phaseGroup === 'passive' },
    { key: 'starting', label: label('stats.starting'), shortLabel: label('stats.startingShort'), value: stats.starting, icon: LuRocket, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30', onClick: () => toggleGroup('starting'), active: phaseGroup === 'starting' },
  ];

  const paneProps = {
    project: selectedProject,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredProjects.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredProjects.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedId(null),
    onBudget: () => setDialog('budget'),
    onContracts: () => setDialog('contracts'),
  };

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <PageHero
          icon={LuFolderKanban}
          gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30"
          title={label('page.title')}
          subtitle={label('page.subtitle')}
        />

        <StatTiles tiles={tiles} ready={hasLoaded} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
                minLength: 1,
              }}
              status={{
                options: [ALL_PHASES, 'active', 'passive', 'starting'].map((value) => ({ value, label: label(`toolbar.groups.${value}`) })),
                value: phaseGroup,
                onChange: setPhaseGroup,
                layoutId: 'project-finance-phase',
                label: label('toolbar.phase'),
              }}
              columnMenu={{
                columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearFilters}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(project) => <ProjectFinanceCardRow project={project} />}
                selectedId={selectedProject?.id}
                onSelect={(project) => setSelectedId(project.id)}
                isLoading={isLoading}
                isBusy={false}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(project, key) => <ProjectFinanceCell columnKey={key} project={project} type={PF_COLUMNS[key].type} />}
                getCellTitle={(project, key) => cellText(project, key, PF_COLUMNS[key].type) || undefined}
                selectedId={selectedProject?.id}
                onSelect={(project) => setSelectedId(project.id)}
                isLoading={isLoading}
                isBusy={false}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={filteredProjects.length}
              rangeOf={label('table.rangeOf', { total: filteredProjects.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: stats.total.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={false}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <ProjectFinanceDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <ProjectFinanceDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key), type: PF_COLUMNS[key].type, options: key === 'projectPhase' ? phaseOptions : undefined }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredProjects.length}
      />

      {dialog === 'budget' && selectedProject && <BudgetModal project={selectedProject} onClose={() => setDialog(null)} />}
      {dialog === 'contracts' && selectedProject && <ContractsModal project={selectedProject} onClose={() => setDialog(null)} />}
    </>
  );
};

export default ProjectsFinanceManagementPage;
